import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, Play, Video } from 'lucide-react';
interface CallDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  callData: {
    id: string;
    talkTime: string;
    callScore: number;
    leadType: string;
    leadSource: string;
    timestamp: Date;
  };
}
interface ExpandableSectionProps {
  title: string;
  preview?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}
function ExpandableSection({
  title,
  preview,
  children,
  defaultOpen = false
}: ExpandableSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return <div className="border-b border-gray-200 last:border-b-0" data-id="element-130">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between py-4 px-5 hover:bg-gray-50 transition-colors" data-id="element-131">
        <div className="flex flex-col items-start gap-1 flex-1" data-id="element-132">
          <span className="font-semibold text-gray-900 text-left" data-id="element-133">{title}</span>
          {preview && !isOpen && <span className="text-xs text-gray-500 font-medium" data-id="element-134">{preview}</span>}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" data-id="element-135" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" data-id="element-136" />}
      </button>

      {isOpen && <div className="px-5 pb-4 text-sm text-gray-600 animate-in slide-in-from-top-2 duration-200" data-id="element-137">
          {children}
        </div>}
    </div>;
}
export function CallDetailsModal({
  isOpen,
  onClose,
  callData
}: CallDetailsModalProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center animate-in fade-in duration-200" data-id="element-138">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} data-id="element-139" />

      {/* Modal */}
      <div className="relative bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col animate-in slide-in-from-bottom-8 sm:slide-in-from-bottom-4 duration-300" data-id="element-140">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200" data-id="element-141">
          <div className="flex items-start gap-4 flex-1" data-id="element-142">
            <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-143">
              <Video className="w-7 h-7 text-gray-600" data-id="element-144" />
            </div>

            <div className="flex-1" data-id="element-145">
              <div className="flex items-baseline gap-3 mb-1" data-id="element-146">
                <div data-id="element-147">
                  <p className="text-xs text-gray-500 font-medium" data-id="element-148">Talk Time</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-149">
                    {callData.talkTime}
                  </p>
                </div>

                <div data-id="element-150">
                  <p className="text-xs text-gray-500 font-medium" data-id="element-151">
                    Call Score
                  </p>
                  <p className="text-2xl font-bold text-green-600" data-id="element-152">
                    {callData.callScore.toFixed(2)}
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-500" data-id="element-153">
                {callData.leadType} • {callData.leadSource}
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-154">
                {callData.timestamp.toLocaleDateString()} at{' '}
                {callData.timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
              </p>
            </div>
          </div>

          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors ml-2" data-id="element-155">
            <X className="w-6 h-6" data-id="element-156" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-157">
          <ExpandableSection title="Call Description Summary" preview="Strong performance • Good rapport" defaultOpen={true} data-id="element-158">
            <p className="leading-relaxed" data-id="element-159">
              This was a successful outbound call where the rep demonstrated
              strong product knowledge and effectively addressed customer
              concerns. The conversation flowed naturally with good rapport
              building and active listening throughout.
            </p>
          </ExpandableSection>

          <ExpandableSection title="Lead Filters" preview={`${callData.leadType} • ${callData.leadSource} • 7/10 difficulty`} data-id="element-160">
            <div className="space-y-2" data-id="element-161">
              <div className="flex justify-between" data-id="element-162">
                <span className="text-gray-500" data-id="element-163">Lead Type:</span>
                <span className="font-medium text-gray-900" data-id="element-164">
                  {callData.leadType}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-165">
                <span className="text-gray-500" data-id="element-166">Lead Source:</span>
                <span className="font-medium text-gray-900" data-id="element-167">
                  {callData.leadSource}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-168">
                <span className="text-gray-500" data-id="element-169">Difficulty:</span>
                <span className="font-medium text-gray-900" data-id="element-170">7/10</span>
              </div>
              <div className="flex justify-between" data-id="element-171">
                <span className="text-gray-500" data-id="element-172">Product Interest:</span>
                <span className="font-medium text-gray-900" data-id="element-173">
                  CRM & Lead Management
                </span>
              </div>
            </div>
          </ExpandableSection>

          <ExpandableSection title="Full Transcription" preview="12 exchanges • 450 words" data-id="element-174">
            <div className="space-y-3 font-mono text-xs" data-id="element-175">
              <div data-id="element-176">
                <span className="font-semibold text-blue-600" data-id="element-177">[Rep]:</span>
                <span className="ml-2" data-id="element-178">
                  Hi, this is calling from ClientCzar. How are you doing today?
                </span>
              </div>
              <div data-id="element-179">
                <span className="font-semibold text-green-600" data-id="element-180">
                  [Customer]:
                </span>
                <span className="ml-2" data-id="element-181">
                  I'm doing well, thanks. What's this about?
                </span>
              </div>
              <div data-id="element-182">
                <span className="font-semibold text-blue-600" data-id="element-183">[Rep]:</span>
                <span className="ml-2" data-id="element-184">
                  I wanted to reach out because I noticed you've been looking at
                  CRM solutions. We have a platform that might be perfect for
                  your needs...
                </span>
              </div>
              <div className="text-gray-400 italic" data-id="element-185">
                [Transcription continues...]
              </div>
            </div>
          </ExpandableSection>

          <ExpandableSection title="Opening Status" preview="4.8/5.0 • Strong opening" data-id="element-186">
            <div className="space-y-2" data-id="element-187">
              <div className="flex items-center gap-2" data-id="element-188">
                <div className="w-3 h-3 bg-green-500 rounded-full" data-id="element-189"></div>
                <span className="font-medium text-gray-900" data-id="element-190">
                  Strong Opening
                </span>
              </div>
              <p className="text-gray-600" data-id="element-191">
                Rep established rapport quickly with a friendly greeting and
                clear value proposition. Customer was receptive and engaged from
                the start.
              </p>
              <div className="mt-3 flex justify-between text-sm" data-id="element-192">
                <span className="text-gray-500" data-id="element-193">Opening Score:</span>
                <span className="font-semibold text-green-600" data-id="element-194">4.8/5.0</span>
              </div>
            </div>
          </ExpandableSection>

          <ExpandableSection title="Time to Open" preview="45s to rapport • 1:20 to value prop" data-id="element-195">
            <div className="space-y-2" data-id="element-196">
              <div className="flex justify-between" data-id="element-197">
                <span className="text-gray-500" data-id="element-198">
                  Time to establish rapport:
                </span>
                <span className="font-medium text-gray-900" data-id="element-199">45 seconds</span>
              </div>
              <div className="flex justify-between" data-id="element-200">
                <span className="text-gray-500" data-id="element-201">Time to value prop:</span>
                <span className="font-medium text-gray-900" data-id="element-202">1:20</span>
              </div>
              <div className="mt-2 p-3 bg-green-50 rounded-lg" data-id="element-203">
                <p className="text-sm text-green-800" data-id="element-204">
                  ✓ Within target range (30-90 seconds)
                </p>
              </div>
            </div>
          </ExpandableSection>

          <ExpandableSection title="Closing Status" preview="4.9/5.0 • Successful close" data-id="element-205">
            <div className="space-y-2" data-id="element-206">
              <div className="flex items-center gap-2" data-id="element-207">
                <div className="w-3 h-3 bg-green-500 rounded-full" data-id="element-208"></div>
                <span className="font-medium text-gray-900" data-id="element-209">
                  Successful Close
                </span>
              </div>
              <p className="text-gray-600" data-id="element-210">
                Rep effectively summarized key benefits, addressed final
                objections, and secured commitment for next steps. Clear
                call-to-action provided.
              </p>
              <div className="mt-3 flex justify-between text-sm" data-id="element-211">
                <span className="text-gray-500" data-id="element-212">Closing Score:</span>
                <span className="font-semibold text-green-600" data-id="element-213">4.9/5.0</span>
              </div>
            </div>
          </ExpandableSection>

          <ExpandableSection title="Time to Close" preview="6:15 to commitment" data-id="element-214">
            <div className="space-y-2" data-id="element-215">
              <div className="flex justify-between" data-id="element-216">
                <span className="text-gray-500" data-id="element-217">Time to commitment:</span>
                <span className="font-medium text-gray-900" data-id="element-218">6:15</span>
              </div>
              <div className="flex justify-between" data-id="element-219">
                <span className="text-gray-500" data-id="element-220">Total call duration:</span>
                <span className="font-medium text-gray-900" data-id="element-221">
                  {callData.talkTime}
                </span>
              </div>
              <div className="mt-2 p-3 bg-blue-50 rounded-lg" data-id="element-222">
                <p className="text-sm text-blue-800" data-id="element-223">
                  ℹ️ Efficient close within optimal timeframe
                </p>
              </div>
            </div>
          </ExpandableSection>

          <ExpandableSection title="Audio Recording" preview={`${callData.talkTime} • MP3 format`} data-id="element-224">
            <div className="space-y-3" data-id="element-225">
              <button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl py-3 px-4 flex items-center justify-center gap-2 hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg" data-id="element-226">
                <Play className="w-5 h-5" data-id="element-227" />
                <span className="font-semibold" data-id="element-228">Listen to Call Recording</span>
              </button>
              <div className="flex items-center justify-between text-sm text-gray-500" data-id="element-229">
                <span data-id="element-230">Duration: {callData.talkTime}</span>
                <span data-id="element-231">Format: MP3</span>
              </div>
            </div>
          </ExpandableSection>

          <ExpandableSection title="Connection Details" preview="Excellent quality • 0 drops" data-id="element-232">
            <div className="space-y-2" data-id="element-233">
              <div className="flex justify-between" data-id="element-234">
                <span className="text-gray-500" data-id="element-235">Connection Duration:</span>
                <span className="font-medium text-gray-900" data-id="element-236">
                  {callData.talkTime}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-237">
                <span className="text-gray-500" data-id="element-238">Ring Time:</span>
                <span className="font-medium text-gray-900" data-id="element-239">8 seconds</span>
              </div>
              <div className="flex justify-between" data-id="element-240">
                <span className="text-gray-500" data-id="element-241">Connection Quality:</span>
                <span className="font-medium text-green-600" data-id="element-242">Excellent</span>
              </div>
              <div className="flex justify-between" data-id="element-243">
                <span className="text-gray-500" data-id="element-244">Dropped Calls:</span>
                <span className="font-medium text-gray-900" data-id="element-245">0</span>
              </div>
            </div>
          </ExpandableSection>

          <ExpandableSection title="Credits & Costs" preview="$0.24 total • 12 credits" data-id="element-246">
            <div className="space-y-3" data-id="element-247">
              <div className="flex justify-between items-center" data-id="element-248">
                <span className="text-gray-500" data-id="element-249">Credits Used:</span>
                <span className="font-bold text-lg text-gray-900" data-id="element-250">12</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-251">
                <span className="text-gray-500" data-id="element-252">LLM Cost:</span>
                <span className="font-bold text-lg text-gray-900" data-id="element-253">$0.24</span>
              </div>
              <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-1 text-xs" data-id="element-254">
                <div className="flex justify-between" data-id="element-255">
                  <span className="text-gray-500" data-id="element-256">Base Call Cost:</span>
                  <span className="text-gray-700" data-id="element-257">$0.15</span>
                </div>
                <div className="flex justify-between" data-id="element-258">
                  <span className="text-gray-500" data-id="element-259">AI Processing:</span>
                  <span className="text-gray-700" data-id="element-260">$0.06</span>
                </div>
                <div className="flex justify-between" data-id="element-261">
                  <span className="text-gray-500" data-id="element-262">Transcription:</span>
                  <span className="text-gray-700" data-id="element-263">$0.03</span>
                </div>
              </div>
            </div>
          </ExpandableSection>
        </div>
      </div>
    </div>;
}